import React, { useState, useMemo, useCallback } from 'react';
import { useFinance } from '../contexts/FinanceContext';
import { formatCurrency, formatDate, groupTransactionsByDate } from '../utils/helpers';
import Modal from './Modal';
import TransactionForm from './TransactionForm';

function TransactionList() {
  const { state, deleteTransaction } = useFinance();
  const [filter, setFilter] = useState('all'); // all, income, expense, transfer
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [modeFilter, setModeFilter] = useState('all');
  
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getCategory = useCallback((id) => {
    return state.categories.find(c => c.id === id) || { icon: '📦', name: 'Other', color: '#64748b' };
  }, [state.categories]);

  const getMode = useCallback((id) => {
    return state.modes.find(m => m.id === id) || { icon: '💳', name: 'Unknown' };
  }, [state.modes]);

  const filteredTransactions = useMemo(() => {
    let result = [...state.transactions];

    // Filter by type
    if (filter !== 'all') {
      result = result.filter(t => t.type === filter);
    }

    // Filter by mode
    if (modeFilter !== 'all') {
      result = result.filter(t => t.modeId === modeFilter || t.targetModeId === modeFilter);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(t => 
        t.description.toLowerCase().includes(query) ||
        getCategory(t.categoryId).name.toLowerCase().includes(query)
      );
    }

    // Filter by date range
    if (dateRange.start) {
      result = result.filter(t => new Date(t.date) >= new Date(dateRange.start));
    }
    if (dateRange.end) {
      result = result.filter(t => new Date(t.date) <= new Date(dateRange.end));
    }

    return result;
  }, [state.transactions, filter, modeFilter, searchQuery, dateRange, getCategory]);

  const groupedTransactions = groupTransactionsByDate(filteredTransactions);

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      deleteTransaction(id);
    }
  };

  return (
    <div className="p-4 lg:p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Transactions</h2>
          <p className="text-slate-400 mt-1">Manage your financial history</p>
        </div>
        <button
          onClick={() => { setEditingTransaction(null); setIsModalOpen(true); }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-primary-600 to-primary-500 text-white font-medium hover:from-primary-500 hover:to-primary-400 transition-all shadow-lg shadow-primary-500/20"
        >
          <span>➕</span> Add Transaction
        </button>
      </div>

      {/* Filters */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 space-y-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
            <input
              type="text"
              placeholder="Search transactions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:border-primary-500"
            />
          </div>
          
          <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
            {['all', 'income', 'expense', 'transfer'].map(type => (
              <button
                key={type}
                onClick={() => setFilter(type)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  filter === type 
                    ? 'bg-primary-500 text-white' 
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">Date:</span>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="px-3 py-1.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
            />
            <span className="text-slate-400">-</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="px-3 py-1.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
            />
          </div>

          <div className="flex items-center gap-2">
             <span className="text-slate-400 text-sm">Mode:</span>
             <select
               value={modeFilter}
               onChange={(e) => setModeFilter(e.target.value)}
               className="px-3 py-1.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm"
             >
                <option value="all">All Modes</option>
                {state.modes.map(mode => (
                  <option key={mode.id} value={mode.id}>{mode.icon} {mode.name}</option>
                ))}
             </select>
          </div>
        </div>
      </div>

      {/* Transaction List */}
      <div className="space-y-6">
        {groupedTransactions.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <span className="text-4xl block mb-2">📄</span>
            No transactions found matching your filters.
          </div>
        ) : (
          groupedTransactions.map(({ date, transactions }) => (
            <div key={date}>
              <h3 className="text-slate-400 text-sm font-medium mb-3 sticky top-0 bg-slate-900 py-2 z-10">
                {formatDate(date)}
              </h3>
              <div className="space-y-3">
                {transactions.map(t => {
                  const category = getCategory(t.categoryId);
                  const mode = getMode(t.modeId);
                  
                  return (
                    <div 
                      key={t.id}
                      className="group bg-slate-800/50 border border-slate-700/50 hover:border-slate-600 rounded-2xl p-4 transition-all hover:bg-slate-800"
                    >
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center text-xl shrink-0"
                          style={{ backgroundColor: `${category.color}20`, color: category.color }}
                        >
                          {category.icon}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="text-white font-medium truncate">{t.description}</h4>
                            {t.tags && t.tags.length > 0 && (
                              <div className="flex gap-1">
                                {t.tags.map(tagId => {
                                  const tag = state.tags.find(tag => tag.id === tagId);
                                  return tag ? (
                                    <span key={tag.id} className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }} title={tag.name} />
                                  ) : null;
                                })}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-slate-400">
                            <span>{category.name}</span>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              {mode.icon} {mode.name}
                              {t.type === 'transfer' && t.targetModeId && (
                                <> ➝ {getMode(t.targetModeId).icon} {getMode(t.targetModeId).name}</>
                              )}
                            </span>
                          </div>
                        </div>

                        <div className={`text-right ${
                          t.type === 'income' ? 'text-emerald-400' : 
                          t.type === 'expense' ? 'text-rose-400' : 
                          'text-blue-400'
                        }`}>
                          <p className="font-bold whitespace-nowrap">
                            {t.type === 'income' ? '+' : t.type === 'expense' ? '-' : ''}
                            {formatCurrency(t.amount, mode.currency || state.currency)}
                          </p>
                          <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button 
                              onClick={() => handleEdit(t)}
                              className="text-slate-400 hover:text-white"
                            >
                              ✏️
                            </button>
                            <button 
                              onClick={() => handleDelete(t.id)}
                              className="text-slate-400 hover:text-rose-400"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        title={editingTransaction ? 'Edit Transaction' : 'New Transaction'}
      >
        <TransactionForm 
          transaction={editingTransaction} 
          onClose={() => setIsModalOpen(false)} 
        />
      </Modal>
    </div>
  );
}

export default TransactionList;
